import { Injectable, signal, computed } from '@angular/core';

export type NotificationCategory = 'prayer' | 'quran' | 'kaza' | 'reminder' | 'system';

export interface AppNotification {
  id: string;
  message: string;
  category: NotificationCategory;
  time: Date;
  read: boolean;
}

const CATEGORY_META: Record<NotificationCategory, { icon: string; label: string; color: string }> = {
  prayer:   { icon: '🕌', label: 'Prayer',   color: '#1f4d3a' },
  quran:    { icon: '📖', label: 'Quran',    color: '#1e3a68' },
  kaza:     { icon: '✅', label: 'Kaza',     color: '#6b1f2a' },
  reminder: { icon: '🔔', label: 'Reminder', color: '#0f5c6b' },
  system:   { icon: '⚙️', label: 'System',   color: '#6b5c1f' },
};

@Injectable({ providedIn: 'root' })
export class NotificationService {
  readonly categoryMeta = CATEGORY_META;

  private readonly _notifications = signal<AppNotification[]>([
    {
      id: '1',
      message: 'Asr prayer time starts in 15 minutes. Prepare for Salah.',
      category: 'prayer',
      time: new Date(Date.now() - 2 * 60 * 1000),
      read: false,
    },
    {
      id: '2',
      message: 'You missed Fajr today. Remember to complete the Kaza prayer.',
      category: 'kaza',
      time: new Date(Date.now() - 4 * 60 * 60 * 1000),
      read: false,
    },
    {
      id: '3',
      message: 'Daily Quran reminder: Read at least one verse to keep your streak going.',
      category: 'quran',
      time: new Date(Date.now() - 7 * 60 * 60 * 1000),
      read: false,
    },
    {
      id: '4',
      message: 'Friday reminder: Recite Surah Al-Kahf today for special blessings.',
      category: 'reminder',
      time: new Date(Date.now() - 24 * 60 * 60 * 1000),
      read: true,
    },
    {
      id: '5',
      message: 'New feature available: Tasbeeh Counter now supports custom Azkar.',
      category: 'system',
      time: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      read: true,
    },
  ]);

  readonly notifications = this._notifications.asReadonly();

  readonly unreadCount = computed(() => this._notifications().filter((n) => !n.read).length);

  markAllRead(): void {
    this._notifications.update((list) => list.map((n) => ({ ...n, read: true })));
  }

  markRead(id: string): void {
    this._notifications.update((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }

  formatTime(time: Date): string {
    const diff = Date.now() - time.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return time.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatDateTime(time: Date): string {
    return time.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  }
}
